import React, { useEffect, lazy } from 'react';
import Calendar from '../Calendar';
import LazyComponent from '../base/LazyComponent';

import CssBaseline from '@mui/material/CssBaseline';

import {
  selectAppConfigProp,
  selectMuiTheme,
  selectSelectedPublicData,
} from '../../slices/appState';
import { useSelector } from 'react-redux';

import CssHandler from './CssHandler';
import FontHandler from './FontHandler';
import DomHandler from './DomHandler';

// PureListView is lightweight and used as fallback - keep it static
import PureListView from '../calendars/PureListView';

// Lazy-loaded calendar frontends
// These are only loaded when the corresponding calendarFrontend config is used
const FullCalendarView = lazy(() => import('../calendars/FullCalendarView'));
const CondensedView = lazy(() => import('../calendars/CondensedView'));
const EyeCandy = lazy(() => import('../calendars/EyeCandy'));
const DetailsView = lazy(() => import('../calendars/DetailsView'));

// I observed flickering of this error when first loading bookingJs
// this should prevent flickering and only show it when its actually needed
function DelayedError({ children }) {
  const [showError, setShowError] = React.useState(null);

  useEffect(() => {
    setTimeout(() => {
      setShowError(true);
    }, 500);
  }, []);

  if (showError) {
    return children;
  }
}

// Convert a viewport breakpoints object into a container-query-based one
// eslint-disable-next-line no-unused-vars
function createContainerBreakpoints(baseBreakpoints) {
  const { values, unit, keys } = baseBreakpoints;
  const step = 0.05;

  const width = (key) => `${values[key]}${unit}`;
  const maxWidth = (key) => `${values[key] - step}${unit}`;

  const up = (key) => `@container (min-width: ${width(key)})`;
  const down = (key) => `@container (max-width: ${maxWidth(key)})`;
  const between = (start, end) =>
    `@container (min-width: ${width(start)}) and (max-width: ${maxWidth(end)})`;
  const only = (key) => {
    const idx = keys.indexOf(key);
    const next = keys[idx + 1];
    return next ? between(key, next) : up(key);
  };

  return {
    values,
    unit,
    keys,
    up,
    down,
    between,
    only,
  };
}

export default function FrontendHandler(props) {
  const publicData = useSelector(selectSelectedPublicData);
  const muiTheme = useSelector(selectMuiTheme);

  const calendarFrontend = useSelector((state) =>
    selectAppConfigProp(state, 'calendarFrontend'),
  );

  const calendarFrontendOptions = useSelector((state) =>
    selectAppConfigProp(state, 'calendarFrontendOptions'),
  );

  // const constrainDialogsToContainer = useSelector((state) =>
  //   selectAppConfigProp(state, 'constrainDialogsToContainer'),
  // );

  // if (constrainDialogsToContainer) {
  //   const viewportBreakpoints = theme.breakpoints;
  //   const containerBreakpoints =
  //     createContainerBreakpoints(viewportBreakpoints);

  //   theme = {
  //     ...theme,
  //     breakpoints: containerBreakpoints,
  //     viewportBreakpoints,
  //   };
  // }

  return (
    <DomHandler theme={muiTheme}>
      <CssBaseline />
      <CssHandler />
      <FontHandler />
      {calendarFrontend === 'fullCalendar' && (
        <Calendar
          {...props}
          {...calendarFrontendOptions['fullCalendar']}
          products={publicData?.products ?? undefined}
        >
          <LazyComponent>
            <FullCalendarView />
          </LazyComponent>
        </Calendar>
      )}
      {calendarFrontend === 'detailsFullCalendar' && (
        <Calendar
          {...props}
          {...calendarFrontendOptions['detailsFullCalendar']}
          products={publicData?.products ?? undefined}
        >
          <LazyComponent>
            <DetailsView CalendarView={FullCalendarView} />
          </LazyComponent>
        </Calendar>
      )}
      {(calendarFrontend === 'pureListView' /* deprecated */ ||
        calendarFrontend === 'listView') && (
        <Calendar
          {...props}
          {...calendarFrontendOptions['pureListView']}
          {...calendarFrontendOptions['listView']}
          products={publicData?.products ?? undefined}
        >
          <PureListView />
        </Calendar>
      )}
      {calendarFrontend === 'detailsListView' && (
        <Calendar
          {...props}
          {...calendarFrontendOptions['detailsListView']}
          products={publicData?.products ?? undefined}
        >
          <LazyComponent>
            <DetailsView CalendarView={PureListView} />
          </LazyComponent>
        </Calendar>
      )}
      {calendarFrontend === 'condensedView' && (
        <Calendar
          {...props}
          {...calendarFrontendOptions['condensedView']}
          suppressResourcePage={
            calendarFrontendOptions['condensedView']?.multiResourceMode || false
          }
          products={publicData?.products ?? undefined}
        >
          <LazyComponent>
            <CondensedView />
          </LazyComponent>
        </Calendar>
      )}
      {calendarFrontend === 'detailsCondensedView' && (
        <Calendar
          {...props}
          {...calendarFrontendOptions['detailsCondensedView']}
          suppressResourcePage={
            calendarFrontendOptions['detailsCondensedView']
              ?.multiResourceMode ||
            calendarFrontendOptions['condensedView']?.multiResourceMode ||
            false
          }
          products={publicData?.products ?? undefined}
        >
          <LazyComponent>
            <DetailsView CalendarView={CondensedView} />
          </LazyComponent>
        </Calendar>
      )}
      {calendarFrontend === 'monthView' && (
        <Calendar
          {...props}
          {...calendarFrontendOptions['monthView']}
          products={publicData?.products ?? undefined}
        >
          <LazyComponent>
            <EyeCandy />
          </LazyComponent>
        </Calendar>
      )}
      {calendarFrontend === 'detailsMonthView' && (
        <Calendar
          {...props}
          {...calendarFrontendOptions['detailsMonthView']}
          products={publicData?.products ?? undefined}
        >
          <LazyComponent>
            <DetailsView CalendarView={EyeCandy} />
          </LazyComponent>
        </Calendar>
      )}
      {publicData && // only show this error if we actually have some data
        calendarFrontend !== 'fullCalendar' &&
        calendarFrontend !== 'detailsFullCalendar' &&
        calendarFrontend !== 'monthView' &&
        calendarFrontend !== 'detailsMonthView' &&
        calendarFrontend !== 'condensedView' &&
        calendarFrontend !== 'detailsCondensedView' &&
        calendarFrontend !== 'detailsListView' &&
        calendarFrontend !== 'listView' &&
        calendarFrontend !== 'pureListView' /* deprecated */ && (
          <DelayedError>
            <>
              You must set calendarFrontend either to <code>fullCalendar</code>,{' '}
              <code>detailsFullCalendar</code>,<code>listView</code>,{' '}
              <code>detailsListView</code>, <code>condensedView</code> or{' '}
              <code>detailsCondensedView</code> <code>monthView</code> or{' '}
              <code>detailsMonthView</code> in your config.
            </>
          </DelayedError>
        )}
    </DomHandler>
  );
}
